var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from 'react';
import Loading from '../../Loading';
import Webcam from 'react-webcam';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Service from '../../../services/Service';
import { useDispatch, useSelector } from 'react-redux';
import { getScaleFactor, drawImageOnCanvas } from '../utils/canvasUtils';
import * as FormActions from '../../../store/form/actions';
import * as LoaderActions from '../../../store/loader/actions';
import { useTranslation } from '../../../i18n';
import imgFace from "./imgs/face.png";
import imgMouthClosed from "./imgs/mouth/closed.png";
import imgMouthSmile from "./imgs/mouth/smile.png";
import imgEyeLeftClosed from "./imgs/eye/left_closed.png";
import imgEyeLeftNormal from "./imgs/eye/left_normal.png";
import imgEyeLeftLookingLeft from "./imgs/eye/left_look_left.png";
import imgEyeLeftLookingRight from "./imgs/eye/left_look_right.png";
import imgEyeRightClosed from "./imgs/eye/right_closed.png";
import imgEyeRightNormal from "./imgs/eye/right_normal.png";
import imgEyeRightLookingLeft from "./imgs/eye/right_look_left.png";
import imgEyeRightLookingRight from "./imgs/eye/right_look_right.png";
import { SubmitChallenge, PoseCanvas, TryAgain, ImagePreview } from './styles';
import { TakePhoto } from '../styles';
var leftEyeInstructions = {
    LOOKING_RIGHT: imgEyeLeftLookingRight,
    LOOKING_LEFT: imgEyeLeftLookingLeft,
    OPEN: imgEyeLeftNormal,
    CLOSED: imgEyeLeftClosed,
};
var rightEyeInstructions = {
    LOOKING_RIGHT: imgEyeRightLookingRight,
    LOOKING_LEFT: imgEyeRightLookingLeft,
    OPEN: imgEyeRightNormal,
    CLOSED: imgEyeRightClosed,
};
var mouthInstructions = {
    SMILE: imgMouthSmile,
    CLOSED: imgMouthClosed
};
var LEFT_EYE_X = 140;
var RIGHT_EYE_X = 354;
var EYE_Y = 290;
var MOUTH_X = 244;
var MOUTH_Y = 556;
var PoseChallenge = function (_a) {
    var step = _a.step, videoConstraints = _a.videoConstraints, eyePose = _a.eyePose, mouthPose = _a.mouthPose, cameraWidth = _a.cameraWidth, cameraHeight = _a.cameraHeight;
    var _b = useSelector(function (state) { return state.form; }), form = _b.form, formInfo = _b.formInfo;
    var t = useTranslation().t;
    var dispatch = useDispatch();
    var navigate = useNavigate();
    var _c = React.useState(false), loading = _c[0], setLoading = _c[1];
    var _d = React.useState(null), challengeImage = _d[0], setChallengeImage = _d[1];
    var webcamRef = React.useRef(null);
    var poseCanvasRef = React.useRef(null);
    var capture = function () {
        if (webcamRef && webcamRef.current) {
            var image = webcamRef.current.getScreenshot();
            setChallengeImage(image);
        }
    };
    var clear = function () {
        setChallengeImage(null);
    };
    var submit = function () {
        setLoading(true);
        var newForm = __assign({}, form);
        var params = {
            tokenOnboarding: (formInfo === null || formInfo === void 0 ? void 0 : formInfo.tokenOnboarding) || '',
            step: newForm.steps[step].step,
            field: "frame_liveness",
            value: challengeImage
        };
        Service.serviceField(params)
            .then(function (response) {
            if (response) {
                var params_1 = {
                    tokenOnboarding: (formInfo === null || formInfo === void 0 ? void 0 : formInfo.tokenOnboarding) || '',
                    step: newForm.steps[step].step,
                    field: "verify_status_liveness",
                };
                Service.serviceField(params_1)
                    .then(function (response) {
                    var _a;
                    if (response) {
                        var livenessResult = (_a = response.find(function (r) { return r.field === "verify_status_liveness"; })) === null || _a === void 0 ? void 0 : _a.value;
                        if (livenessResult === "true") {
                            response.forEach(function (r) {
                                switch (r.field) {
                                    case "challenge_type_liveness":
                                        r.value = "POSE";
                                        break;
                                    case "pose_eyes_liveness":
                                        r.value = eyePose;
                                        break;
                                    case "pose_mouth_liveness":
                                        r.value = mouthPose;
                                }
                                ;
                            });
                            var stepParams = {
                                onboardingForm: {
                                    countSteps: form.countSteps,
                                    steps: [__assign(__assign({}, form.steps[step]), { fields: response, fieldsComplete: [
                                                "verify_status_liveness",
                                                "challenge_type_liveness",
                                                "pose_eyes_liveness",
                                                "pose_mouth_liveness"
                                            ] })],
                                },
                                tokenOnboarding: (formInfo === null || formInfo === void 0 ? void 0 : formInfo.tokenOnboarding) || '',
                            };
                            Service.saveStep(stepParams)
                                .then(function (response) {
                                dispatch(LoaderActions.show(false));
                                setChallengeImage(null);
                                setLoading(true);
                                if (response) {
                                    dispatch(FormActions.addFormStep(response));
                                    navigate('/form', { state: { step: step + 1 } });
                                }
                            })
                                .catch(function () {
                                dispatch(LoaderActions.show(false));
                                setChallengeImage(null);
                            });
                        }
                    }
                })
                    .catch(function (err) {
                    setChallengeImage(null);
                    if (err && err.title) {
                        toast.error(err.title, {
                            position: "top-center",
                            autoClose: 3000,
                            hideProgressBar: true,
                            closeOnClick: true,
                            pauseOnHover: false,
                            draggable: true,
                            progress: undefined,
                        });
                    }
                });
            }
            setLoading(false);
        });
    };
    var drawFace = React.useCallback(function () {
        if (poseCanvasRef && poseCanvasRef.current) {
            var canvas_1 = poseCanvasRef.current;
            var context_1 = canvas_1.getContext("2d");
            var face_image_1 = new Image();
            face_image_1.src = imgFace;
            face_image_1.onload = function () {
                var scaleFactor = getScaleFactor(canvas_1, face_image_1);
                canvas_1.width = face_image_1.width * scaleFactor;
                canvas_1.height = face_image_1.height * scaleFactor;
                var marginX = (canvas_1.width - face_image_1.width * scaleFactor) / 2;
                var marginY = (canvas_1.height - face_image_1.height * scaleFactor) / 2;
                if (context_1) {
                    context_1.drawImage(face_image_1, marginX, marginY, face_image_1.width * scaleFactor, face_image_1.height * scaleFactor);
                    // LEFT EYE
                    drawImageOnCanvas(canvas_1, leftEyeInstructions[eyePose], LEFT_EYE_X * scaleFactor + marginX, EYE_Y * scaleFactor + marginY, scaleFactor);
                    // RIGHT EYE
                    drawImageOnCanvas(canvas_1, rightEyeInstructions[eyePose], RIGHT_EYE_X * scaleFactor + marginX, EYE_Y * scaleFactor + marginY, scaleFactor);
                    // MOUTH
                    drawImageOnCanvas(canvas_1, mouthInstructions[mouthPose], MOUTH_X * scaleFactor + marginX, MOUTH_Y * scaleFactor + marginY, scaleFactor);
                }
            };
        }
    }, []);
    React.useEffect(function () {
        drawFace();
    }, [challengeImage]);
    return (React.createElement(React.Fragment, null, !challengeImage ? (React.createElement(React.Fragment, null,
        React.createElement(Webcam, { mirrored: true, audio: false, ref: webcamRef, width: cameraWidth, height: cameraHeight, screenshotQuality: 0.98, forceScreenshotSourceSize: true, screenshotFormat: "image/jpeg", videoConstraints: videoConstraints, style: {
                objectFit: "cover",
                height: cameraHeight,
                width: cameraWidth
            } }),
        React.createElement(PoseCanvas, { ref: poseCanvasRef, style: {
                top: "".concat(cameraHeight * 0.03, "px"),
                right: "".concat(cameraWidth * 0.03, "px")
            } }),
        React.createElement(TakePhoto, { text: t('selfie.takePhoto'), onClick: capture }))) : (React.createElement(React.Fragment, null,
        React.createElement(ImagePreview, { src: challengeImage }),
        React.createElement(TryAgain, { text: t('common.tryAgain'), onClick: clear }),
        React.createElement(SubmitChallenge, { text: t('common.confirm'), onClick: submit, disabled: loading }),
        loading && (React.createElement(Loading, { type: "bubbles" }))))));
};
export default PoseChallenge;
